"use client";

import RevealOnScroll from "@/components/atoms/RevealOnScroll";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { formatBackgroundStyle } from "@/utils/styles/format";
import { FC, useMemo, useState } from "react";
import * as z from "@/lib/propSchema";

export const COURSE_OUTCOME_1_FONT_FAMILY_MAP_VARIANT_1 = {
  preTitle: "var(--font-poppins)",
  title: "var(--font-poppins)",
  description: "var(--font-poppins)",
  loadMore: "var(--font-poppins)",
  cardMetaTitle: "var(--font-poppins)",
  cardMetaDescription: "var(--font-poppins)",
  cardBoxTitle: "var(--font-poppins)",
  cardBoxSubTitle: "var(--font-poppins)",
  sectionPreTitleClassName:
    "text-[16px] sm:text-[17px] md:text-[18px] lg:text-[22px] xl:text-[25px]",
  sectionTitleClassName:
    "text-[24px] sm:text-[26px] md:text-[28px] lg:text-[36px] xl:text-[42px]",
} as const;


export const COURSE_OUTCOME_1_FONT_FAMILY_MAP_VARIANT_2 = {
  preTitle: "var(--font-google-sans)",
  title: "var(--font-merriweather)",
  description: "var(--font-google-sans)",
  loadMore: "var(--font-google-sans)",
  cardMetaTitle: "var(--font-google-sans)",
  cardMetaDescription: "var(--font-google-sans)",
  cardBoxTitle: "var(--font-merriweather)",
  cardBoxSubTitle: "var(--font-google-sans)",
  sectionPreTitleClassName:
  "text-[24px] min-[1080px]:text-[28px] min-[1230px]:text-[30px] min-[1430px]:text-[40px]",
sectionTitleClassName:
  "text-[40px] min-[1080px]:text-[44px] min-[1230px]:text-[54px] min-[1430px]:text-[64px]",
} as const;

export type CourseOutcome1FontFamilyMap =
  | typeof COURSE_OUTCOME_1_FONT_FAMILY_MAP_VARIANT_1
  | typeof COURSE_OUTCOME_1_FONT_FAMILY_MAP_VARIANT_2;

const MetadataSchema = z.object({
  id: z.union([z.number(), z.string()]),
  meta_title: z.string(),
  meta_title_color: z.string().optional().default(""),
  meta_description: z.string().optional().default(""),
  meta_description_color: z.string().optional().default(""),
  meta_box_color: z.string().optional().default(""),
  meta_box_title: z.string().optional().default(""),
  meta_box_title_color: z.string().optional().default(""),
  meta_box_sub_title: z.string().optional().default("Avg Salary"),
  meta_box_sub_title_color: z.string().optional().default(""),
  priority: z.coerce.number().optional().default(0),
});

type MetadataType = z.infer<typeof MetadataSchema>;

const CourseOutcome1DataSchema = z.object({
  desktop_background_color: z.string().optional().default(""),
  mobile_background_color: z.string().optional().default(""),
  backvariant: z.enum(["variant_1", "variant_2", "variant_3"]).optional().default("variant_3"),
  font_family_variant: z
    .enum(["variant_1", "variant_2"])
    .catch("variant_2"),
  font_style: z.string().optional(),
  gradient_color_1: z.string().optional().default(""),
  gradient_color_2: z.string().optional().default(""),
  gradient_color_3: z.string().optional().default(""),
  gradient_color_4: z.string().optional().default(""),
  pre_title: z.string().optional().default("Program Outcome"),
  pre_title_text_color: z.string().optional().default(""),
  title: z.string().optional().default("Program Outcome"),
  title_gradient_angle: z.string().optional().default(""),
  title_gradient_start_color: z.string().optional().default(""),
  title_gradient_end_color: z.string().optional().default(""),
  description: z.string().optional().default(""),
  description_color: z.string().optional().default(""),
  read_more_text: z.string().optional().default("Load More"),
  read_more_color: z.string().optional().default(""),
  load_more_button: z.string().nullable().optional(),
  load_more_button_text_color: z.string().nullable().optional(),
  metadata: z.array(MetadataSchema).optional().default([]),
  variant: z.union([z.string(), z.number()]).optional().default(1),
  enable_animation: z.union([z.boolean(), z.number()]).optional().default(false),
}).transform(data => {
  // sort metadata by priority
  return {
    ...data,
    metadata: data.metadata?.sort((a: MetadataType, b: MetadataType) => (a.priority || 0) - (b.priority || 0)),
  };
});

type Metadata = z.infer<typeof MetadataSchema>;

// Career Card Component
const CareerCard: FC<{ role: Metadata; fonts: CourseOutcome1FontFamilyMap }> = ({
  role,
  fonts,
}) => {
  return (
    <div className={cn(
      "content-stretch flex items-stretch relative w-full h-full"
    )}>
      {/* Left Section - Role Info */}
      <div className={cn(
        "bg-[rgba(255,255,255,0.15)] content-stretch flex flex-[1_0_0] flex-col items-start justify-start leading-[1.49] min-h-px min-w-px relative",
        // xs (Mobile) - padding 10px, gap 8px
        "p-[10px] gap-[8px]",
        // sm (1080px) - padding 15px, gap 6px
        "sm:p-[15px] sm:gap-[6px]",
        // md (1230px) - padding 16px, gap 7px
        "md:p-[16px] md:gap-[7px]",
        // lg (1430px) - padding 20px, gap 8px
        "lg:p-[20px] lg:gap-[8px]",
        // xl - padding 20px, gap 8px
        "xl:p-[20px] xl:gap-[8px]"
      )}>
        {/* Card Title - Max lines: 1 */}
        <p className={cn(
          "font-semibold relative shrink-0 text-[#56e2f5] w-full line-clamp-2",
          // xs - 16px
          "text-[16px]",
          // sm - 13px
          "sm:text-[13px]",
          // md - 14px
          "md:text-[14px]",
          // lg - 18px
          "lg:text-[18px]",
          // xl - 18px
          "xl:text-[18px]"
        )}
          style={{
            fontFamily: fonts.cardMetaTitle,
            color: role.meta_title_color || "#56e2f5",
          }}
        >
          {role.meta_title}
        </p>

        {/* Card Sub-title - Max lines: 2 */}
        {role.meta_description && (
          <p className={cn(
            "font-normal relative shrink-0 text-white w-full line-clamp-4 sm:line-clamp-2",
            // xs - 14px
            "text-[14px]",
            // sm - 10px
            "sm:text-[10px]",
            // md - 12px
            "md:text-[12px]",
            // lg - 14px
            "lg:text-[14px]",
            // xl - 14px
            "xl:text-[14px]"
          )}
            style={{
              fontFamily: fonts.cardMetaDescription,
              color: role.meta_description_color || "#FFFFFF",
            }}
          >
            {role.meta_description}
          </p>
        )}
      </div>

      {/* Right Section - Salary Info */}
      {role.meta_box_title && (
        <div className={cn(
          "bg-white content-stretch flex flex-col items-center justify-center relative self-stretch shrink-0 text-center",
          // xs - padding 10px, gap 8px, width 90px
          "w-[90px] p-[10px] gap-[8px]",
          // sm - padding 15px, gap 6px, width 111px
          "sm:w-[111px] sm:p-[15px] sm:gap-[6px]",
          // md - padding 16px, gap 6px, width 133px
          "md:w-[133px] md:p-[16px] md:gap-[6px]",
          // lg - padding 20px, gap 8px, width 161px
          "lg:w-[161px] lg:p-[20px] lg:gap-[8px]",
          // xl - padding 20px, gap 8px, width 161px
          "xl:w-[161px] xl:p-[20px] xl:gap-[8px]"
        )}
          style={{
            backgroundColor: role.meta_box_color || "#FFFFFF"
          }}
        >
          {/* Salary Range Title - Max lines: 1 */}
          <p className={cn(
            "font-bold leading-[1.2] relative shrink-0 text-[#342b7c] w-full sm:line-clamp-2",
            // xs - 20px, tracking 0.6px
            "text-[20px] tracking-[0.6px]",
            // sm - 16px, tracking 0.48px
            "sm:text-[16px] sm:tracking-[0.48px]",
            // md - 20px, tracking 0.6px
            "md:text-[20px] md:tracking-[0.6px]",
            // lg - 24px, tracking 0.72px
            "lg:text-[24px] lg:tracking-[0.72px]",
            // xl - 24px, tracking 0.72px
            "xl:text-[24px] xl:tracking-[0.72px]"
          )}
            style={{
              fontFamily: fonts.cardBoxTitle,
              color: role.meta_box_title_color || "#342b7c",
            }}
          >
            {role.meta_box_title}
          </p>

          {/* Salary Range Sub-title - Max lines: 1 */}
          <p className={cn(
            "font-normal leading-[1.49] relative shrink-0 text-[#222] w-full line-clamp-1",
            // xs - 14px
            "text-[14px]",
            // sm - 10px
            "sm:text-[10px]",
            // md - 12px
            "md:text-[12px]",
            // lg - 14px
            "lg:text-[14px]",
            // xl - 14px
            "xl:text-[14px]"
          )}
            style={{
              fontFamily: fonts.cardBoxSubTitle,
              color: role.meta_box_sub_title_color || "#222222",
            }}
          >
            {role.meta_box_sub_title}
          </p>
        </div>
      )}
    </div>
  );
};

export const CourseOutcome1: FC<any> = (props) => {
  const actualData = props?.data?.data || props?.data || props;

  const isMobile = useIsMobile();
  const [mobileExpanded, setMobileExpanded] = useState(false);

  const parsedResult = useMemo(() => CourseOutcome1DataSchema.safeParse(actualData), [actualData]);

  if (!parsedResult.success) {
    console.error("validation failed", parsedResult.error);
    return null;
  }

  const parsedData = parsedResult.data;

  const isAnimationEnabled =
    parsedData.enable_animation === true || parsedData.enable_animation === 1;

  const fonts = useMemo((): CourseOutcome1FontFamilyMap => {
    return parsedData.font_family_variant === "variant_1"
      ? COURSE_OUTCOME_1_FONT_FAMILY_MAP_VARIANT_1
      : COURSE_OUTCOME_1_FONT_FAMILY_MAP_VARIANT_2;
  }, [parsedData.font_family_variant]);

  // Determine cards to display: on mobile (xs) show 3 until "Read more" clicked, then all
  const cardsToDisplay = useMemo(() => {
    if (!parsedData.metadata || parsedData.metadata.length === 0) return [];
    return parsedData.metadata;
  }, [parsedData.metadata]);

  // Mobile (sm:hidden): show 3 until "Read more" clicked, then all. No isMobile dependency.
  const mobileCardsToShow = !mobileExpanded
    ? cardsToDisplay.slice(0, 3)
    : cardsToDisplay;
  const showReadMoreButton = cardsToDisplay.length > 3 && !mobileExpanded;
  const readMoreLabel = parsedData.load_more_button ?? parsedData.read_more_text ?? "Read More";

  const bgColor = formatBackgroundStyle(parsedData, isMobile || false, "#000000");

  return (
    <section
      className={cn(
        "relative w-full overflow-hidden flex justify-center",
        "px-[24px] py-[40px]",
        "sm:px-[60px] sm:py-[40px]",
        "md:px-[60px] md:py-[60px]",
        "lg:px-[80px] lg:py-[60px]",
        "xl:px-[140px] xl:py-[80px]"
      )}
      style={bgColor}
    >
      <div className={cn(
        "xs:w-[354px]",
        "sm:w-[960px]",
        "md:w-[1110px]",
        "lg:w-[1270px]",
        "xl:w-[1470px]"
      )}>
        {/* Title Section - Max lines: 2 total (pre_title + title). One <h2> for both
            lines; spans because a heading may only hold phrasing content (the flex
            column blockifies them). */}
        <h2
          className={cn(
            "relative flex flex-col mb-[24px]",
            "sm:mb-[27px]",
            "lg:mb-[36px]"
          )}
        >
          <span
            className={cn(
              "font-semibold leading-normal line-clamp-1",
              fonts.sectionPreTitleClassName
            )}
            style={{
              color: parsedData.pre_title_text_color,
              fontFamily: fonts.preTitle,
            }}
          >
            {parsedData.pre_title}
          </span>
          <span
            className={cn(
              "font-bold leading-[1.2] line-clamp-1 bg-clip-text text-transparent",
              fonts.sectionTitleClassName
            )}
            style={{
              backgroundImage: parsedData.title_gradient_angle && parsedData.title_gradient_start_color && parsedData.title_gradient_end_color ? 
                          `linear-gradient(${parsedData.title_gradient_angle}, ${parsedData.title_gradient_start_color}, ${parsedData.title_gradient_end_color})` : 
                          undefined,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontFamily: fonts.title,
            }}
          >
            {parsedData.title}
          </span>
        </h2>

        {/* Description Paragraph */}
        {parsedData.description && (
          <p
            className={cn(
              "relative font-semibold leading-[1.49] line-clamp-3",
              // Mobile (xs) - 14px
              "text-[14px] w-full font-stretch-100% mb-[24px]",
              // Small tablet (sm) - 14px, 1080px breakpoint
              "sm:text-[14px] sm:w-[709px] sm:mb-[24px]",
              // Medium (md) - 16px
              "md:text-[16px] md:w-[808px] md:mb-[24px]",
              // Large (lg) - 18px, 1430px breakpoint
              "lg:text-[18px] lg:w-[919px] lg:mb-[36px]",
              // Extra large (xl) - 18px
              "xl:text-[18px] xl:w-[919px] xl:mb-[36px]"
            )}
            style={{
              fontFamily: fonts.description,
              color: parsedData.description_color,
            }}
          >
            {parsedData.description}
          </p>
        )}

        {/* Career Roles Grid */}
        {parsedData.metadata && parsedData.metadata.length > 0 && (
          <>
            {/* Mobile View - 3 cards until "Read more" clicked */}
            <div className={cn(
              "relative grid w-full sm:hidden",
              "grid-cols-1 gap-[24px]"
            )}>
              {mobileCardsToShow.map((role) => (
                <RevealOnScroll key={role.id} enableAnimation={isAnimationEnabled}>
                  <CareerCard  role={role} fonts={fonts} />
                </RevealOnScroll>
              ))}
            </div>

            {/* Read More - xs only, Figma: text + underline under text only, no background */}
            {showReadMoreButton && (
              <button
                type="button"
                data-cta-name="view_more"
                onClick={() => setMobileExpanded(true)}
                className={cn(
                  "sm:hidden w-full max-w-[354px] flex items-center justify-center font-semibold mt-[24px]",
                  "text-[16px] leading-[24px] tracking-[0.16px]",
                  "text-white underline underline-offset-2"
                )}
                style={{
                  fontFamily: fonts.loadMore,
                  color:
                    parsedData.load_more_button_text_color ??
                    parsedData.read_more_color ??
                    "#FFFFFF",
                  textDecorationColor:
                    parsedData.load_more_button_text_color ??
                    parsedData.read_more_color ??
                    "#FFFFFF",
                }}
              >
                {readMoreLabel}
              </button>
            )}

            {/* Desktop View - All Cards */}
            <div className={cn(
              "relative w-full hidden sm:grid",
              // sm (1080px) - 2 columns, gap 30px
              "sm:grid-cols-2 sm:gap-[30px]",
              // md (1230px) - 2 columns, gap 24px
              "md:grid-cols-2 md:gap-[24px]",
              // lg (1430px) - 2 columns, gap 40px
              "lg:grid-cols-2 lg:gap-[40px]",
              // xl - 2 columns, gap 40px
              "xl:grid-cols-2 xl:gap-[40px]"
            )}>
              {parsedData.metadata.map((role, indx) => (
                <RevealOnScroll
                  key={role.id}
                  y={-50}
                  scaleStart={0.8}
                  x={indx % 2 === 0 ? -200 : 200}
                  enableAnimation={isAnimationEnabled}
                >
                  <CareerCard role={role} fonts={fonts} />
                </RevealOnScroll>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};